import { makeAutoObservable, runInAction, reaction, when } from "mobx";
import type RootStore from "./RootStore";
import type ServiceStore from "./ServiceStore";

export interface ConversationThread {
  id: number;
  title: string;
  description?: string;
  messageCount: number;
  lastMessageAt: string;
  isEphemeral: boolean;
  status: "active" | "archived";
  userId: number;
  sharedWith?: number[];
  createdAt: string;
  updatedAt: string;
}

export interface ChatMessage {
  id: string;
  threadId: number;
  content: string;
  role: "user" | "assistant" | "system";
  userId?: number;
  messageOrder: number;
  metadata?: {
    model?: string;
    temperature?: number;
    maxTokens?: number;
    topP?: number;
    frequencyPenalty?: number;
    presencePenalty?: number;
    [key: string]: any;
  };
  relatedEntities?: Array<{
    entityType: string;
    entityId: string | number;
    entityName?: string;
    relationship?: string;
  }>;
  entityMetadata?: Record<string, any>;
  parentMessageId?: string;
  isStreaming?: boolean;
  error?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SendMessageOptions {
  parentMessageId?: string;
  metadata?: ChatMessage["metadata"];
  relatedEntities?: ChatMessage["relatedEntities"];
  entityMetadata?: ChatMessage["entityMetadata"];
}

export interface TypingUser {
  userId: number;
  userName: string;
  startedAt: Date;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  hasMore: boolean;
  total?: number;
}

class ConversationStore {
  rootStore: RootStore;
  // Observables - Thread Management
  threads: ConversationThread[] = [];
  currentThread: ConversationThread | null = null;
  currentThreadId: number | null = null;
  searchResults: ConversationThread[] = [];
  isSearching = false;
  threadFilter: "all" | "active" | "archived" | "shared" = "active";

  // Observables - Message Management
  messages: Map<number, ChatMessage[]> = new Map();
  optimisticMessages: Map<string, ChatMessage> = new Map();
  messagePages: Map<number, PaginationInfo> = new Map();
  streamingMessages: Map<string, ChatMessage> = new Map();

  // Observables - UI State
  loadingStates: Map<string, boolean> = new Map();
  errors: Map<string, Error> = new Map();
  typingUsers: Map<number, TypingUser[]> = new Map();

  // Observables - Real-time State
  isConnected = false;
  reconnectAttempts = 0;
  lastSyncTimestamp: Date | null = null;

  // Private properties
  private socketListeners: Map<string, Function> = new Map();
  private typingTimeouts: Map<string, NodeJS.Timeout> = new Map();
  private messageCache: Map<string, ChatMessage> = new Map();
  private pendingOperations: Set<string> = new Set();
  private threadStore: ServiceStore | null = null;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, {
      rootStore: false,
      socketListeners: false,
      typingTimeouts: false,
      messageCache: false,
      pendingOperations: false,
      threadStore: false
    } as any);
    this.rootStore = rootStore;
    // Access the conversation-threads store - it might not be initialized yet
    this.threadStore = (rootStore as any).conversationThreadsStore || null;
    this.initializeSocketListeners();
    this.setupReactions();
  }

  // Actions - Thread Management
  setCurrentThread(thread: ConversationThread | null) {
    this.currentThread = thread;
    this.currentThreadId = thread?.id || null;
    if (thread && !this.messages.has(thread.id)) {
      this.messages.set(thread.id, []);
      this.messagePages.set(thread.id, { page: 1, limit: 50, hasMore: true });
    }
  }

  setThreadFilter(filter: "all" | "active" | "archived" | "shared") {
    this.threadFilter = filter;
    this.loadThreads();
  }

  updateThread(threadId: number, updates: Partial<ConversationThread>) {
    const threadIndex = this.threads.findIndex((t) => t.id === threadId);
    if (threadIndex !== -1) {
      this.threads[threadIndex] = { ...this.threads[threadIndex], ...updates };
    }
    if (this.currentThread?.id === threadId) {
      this.currentThread = { ...this.currentThread, ...updates };
    }
  }

  // Actions - Loading State Management
  setLoading(key: string, value: boolean) {
    if (value) {
      this.loadingStates.set(key, true);
    } else {
      this.loadingStates.delete(key);
    }
  }

  setError(key: string, error: Error | null) {
    if (error) {
      this.errors.set(key, error);
      console.error(`[ConversationStore] Error in ${key}:`, error);
    } else {
      this.errors.delete(key);
    }
  }

  clearError(key: string) {
    this.errors.delete(key);
  }

  clearAllErrors() {
    this.errors.clear();
  }

  // Actions - Message Management
  addOptimisticMessage(message: ChatMessage) {
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    const optimisticMsg = { ...message, id: tempId };
    this.optimisticMessages.set(tempId, optimisticMsg);

    const threadMessages = this.messages.get(message.threadId) || [];
    this.messages.set(message.threadId, [...threadMessages, optimisticMsg]);

    return tempId;
  }

  replaceOptimisticMessage(tempId: string, realMessage: ChatMessage) {
    const optimisticMsg = this.optimisticMessages.get(tempId);
    if (optimisticMsg) {
      const threadMessages = this.messages.get(optimisticMsg.threadId) || [];
      const index = threadMessages.findIndex((m) => m.id === tempId);
      if (index !== -1) {
        threadMessages[index] = realMessage;
        this.messages.set(optimisticMsg.threadId, [...threadMessages]);
      }
      this.optimisticMessages.delete(tempId);
    }
  }

  removeOptimisticMessage(tempId: string) {
    const optimisticMsg = this.optimisticMessages.get(tempId);
    if (optimisticMsg) {
      const threadMessages = this.messages.get(optimisticMsg.threadId) || [];
      this.messages.set(
        optimisticMsg.threadId,
        threadMessages.filter((m) => m.id !== tempId)
      );
      this.optimisticMessages.delete(tempId);
    }
  }

  updateStreamingMessage(
    messageId: string,
    content: string,
    isComplete = false
  ) {
    const message = this.streamingMessages.get(messageId);
    if (message) {
      message.content = content;
      message.isStreaming = !isComplete;

      if (isComplete) {
        this.streamingMessages.delete(messageId);
      }

      const threadMessages = this.messages.get(message.threadId) || [];
      const index = threadMessages.findIndex((m) => m.id === messageId);
      if (index !== -1) {
        threadMessages[index] = { ...message };
        this.messages.set(message.threadId, [...threadMessages]);
      }
    }
  }

  // Actions - Typing Indicators
  addTypingUser(threadId: number, user: TypingUser) {
    const typingInThread = this.typingUsers.get(threadId) || [];
    const existingIndex = typingInThread.findIndex(
      (u) => u.userId === user.userId
    );

    if (existingIndex !== -1) {
      typingInThread[existingIndex] = user;
    } else {
      typingInThread.push(user);
    }

    this.typingUsers.set(threadId, typingInThread);

    // Auto-remove after 5 seconds
    const timeoutKey = `${threadId}-${user.userId}`;
    const existingTimeout = this.typingTimeouts.get(timeoutKey);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    const timeout = setTimeout(() => {
      this.removeTypingUser(threadId, user.userId);
    }, 5000);

    this.typingTimeouts.set(timeoutKey, timeout);
  }

  removeTypingUser(threadId: number, userId: number) {
    const typingInThread = this.typingUsers.get(threadId) || [];
    this.typingUsers.set(
      threadId,
      typingInThread.filter((u) => u.userId !== userId)
    );

    const timeoutKey = `${threadId}-${userId}`;
    const timeout = this.typingTimeouts.get(timeoutKey);
    if (timeout) {
      clearTimeout(timeout);
      this.typingTimeouts.delete(timeoutKey);
    }
  }

  // Actions - Connection State
  setConnectionState(connected: boolean) {
    this.isConnected = connected;
    if (connected) {
      this.reconnectAttempts = 0;
      this.lastSyncTimestamp = new Date();
    }
  }

  incrementReconnectAttempts() {
    this.reconnectAttempts++;
  }

  // Async actions - Thread Operations
  async loadThreads() {
    const loadingKey = `threads-${this.threadFilter}`;
    this.setLoading(loadingKey, true);
    this.setError(loadingKey, null);

    try {
      let threads: ConversationThread[] = [];

      // Use direct API calls
      
      switch (this.threadFilter) {
        case "all":
          threads = await this.getThreadsFromStore(true);
          break;
        case "archived":
          threads = await this.getThreadsFromStore(true);
          threads = threads.filter((t) => t.status === "archived");
          break;
        case "shared":
          threads = await this.getSharedThreadsFromStore();
          break;
        case "active":
        default:
          threads = await this.getThreadsFromStore(false);
          break;
      }

      runInAction(() => {
        this.threads = threads;
      });
    } catch (error) {
      this.setError(loadingKey, error as Error);
    } finally {
      this.setLoading(loadingKey, false);
    }
  }

  async createThread(data?: {
    title?: string;
    description?: string;
    isEphemeral?: boolean;
  }) {
    const operationId = `create-thread-${Date.now()}`;
    this.pendingOperations.add(operationId);
    this.setLoading("createThread", true);
    this.setError("createThread", null);

    try {
      // Optimistic update
      const tempThread: ConversationThread = {
        id: Date.now(),
        title: data?.title || "New Conversation",
        description: data?.description,
        messageCount: 0,
        lastMessageAt: new Date().toISOString(),
        isEphemeral: data?.isEphemeral !== false,
        status: "active",
        userId: (this.rootStore as any).authStore?.currentUser?.id || 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      runInAction(() => {
        this.threads.unshift(tempThread);
        this.setCurrentThread(tempThread);
      });

      // Actual API call
      const thread = await this.createThreadAPI(data);

      runInAction(() => {
        // Replace optimistic thread with real one
        const index = this.threads.findIndex((t) => t.id === tempThread.id);
        if (index !== -1) {
          this.threads[index] = thread;
        }
        this.setCurrentThread(thread);
      });

      return thread;
    } catch (error) {
      // Rollback optimistic update
      runInAction(() => {
        this.threads = this.threads.filter(
          (t) => t.id !== this.currentThread?.id
        );
        this.setCurrentThread(null);
      });
      this.setError("createThread", error as Error);
      throw error;
    } finally {
      this.setLoading("createThread", false);
      this.pendingOperations.delete(operationId);
    }
  }

  async loadMessages(threadId: number, reset = false) {
    const loadingKey = `messages-${threadId}`;
    this.setLoading(loadingKey, true);
    this.setError(loadingKey, null);

    try {
      const pageInfo = this.messagePages.get(threadId) || {
        page: 1,
        limit: 50,
        hasMore: true,
      };

      if (reset) {
        pageInfo.page = 1;
        pageInfo.hasMore = true;
      }

      const messages = await this.getThreadMessagesFromStore(
        threadId,
        pageInfo.limit,
        (pageInfo.page - 1) * pageInfo.limit
      );

      runInAction(() => {
        if (reset || pageInfo.page === 1) {
          this.messages.set(threadId, messages);
        } else {
          const existingMessages = this.messages.get(threadId) || [];
          this.messages.set(threadId, [...existingMessages, ...messages]);
        }

        pageInfo.hasMore = messages.length === pageInfo.limit;
        this.messagePages.set(threadId, pageInfo);
      });

      return messages;
    } catch (error) {
      this.setError(loadingKey, error as Error);
      throw error;
    } finally {
      this.setLoading(loadingKey, false);
    }
  }

  async loadMoreMessages(threadId: number) {
    const pageInfo = this.messagePages.get(threadId);
    if (!pageInfo || !pageInfo.hasMore) {
      return [];
    }

    pageInfo.page++;
    this.messagePages.set(threadId, pageInfo);

    return this.loadMessages(threadId);
  }

  async sendMessage(
    threadId: number,
    content: string,
    options: SendMessageOptions = {}
  ): Promise<ChatMessage> {
    const operationId = `send-${threadId}-${Date.now()}`;
    this.pendingOperations.add(operationId);
    this.setLoading(`sendMessage-${threadId}`, true);
    this.setError(`sendMessage-${threadId}`, null);

    // Create optimistic message
    const optimisticMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      threadId,
      content,
      role: "user",
      userId: (this.rootStore as any).authStore?.currentUser?.id,
      messageOrder: (this.messages.get(threadId)?.length || 0) + 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ...options,
    };

    const tempId = this.addOptimisticMessage(optimisticMessage);

    try {
      const chatMessagesStore = (this.rootStore as any).chatMessagesStore;
      if (!chatMessagesStore) {
        throw new Error("Chat messages store not available");
      }

      // Send typing stopped event
      this.emitTypingStopped(threadId);

      // Send actual message
      const message = await chatMessagesStore.create({
        threadId,
        content,
        role: "user",
        ...options,
      });

      runInAction(() => {
        // Replace optimistic message with real one
        this.replaceOptimisticMessage(tempId, message as ChatMessage);

        // Update thread's last message timestamp
        this.updateThread(threadId, {
          lastMessageAt: message.createdAt,
          messageCount: (this.currentThread?.messageCount || 0) + 1,
        });
      });

      return message as ChatMessage;
    } catch (error) {
      // Remove optimistic message on error
      runInAction(() => {
        this.removeOptimisticMessage(tempId);
      });
      this.setError(`sendMessage-${threadId}`, error as Error);
      throw error;
    } finally {
      this.setLoading(`sendMessage-${threadId}`, false);
      this.pendingOperations.delete(operationId);
    }
  }

  async searchThreads(searchTerm: string) {
    if (!searchTerm.trim()) {
      runInAction(() => {
        this.searchResults = [];
        this.isSearching = false;
      });
      return;
    }

    this.setLoading("search", true);
    this.isSearching = true;

    try {
      const results = await this.searchThreadsAPI(searchTerm);
      runInAction(() => {
        this.searchResults = results;
      });
    } catch (error) {
      this.setError("search", error as Error);
    } finally {
      this.setLoading("search", false);
      this.isSearching = false;
    }
  }

  async saveThread(threadId: number, title: string, description?: string) {
    const operationId = `save-thread-${threadId}`;
    this.pendingOperations.add(operationId);
    this.setLoading(`saveThread-${threadId}`, true);
    this.setError(`saveThread-${threadId}`, null);

    try {
      const savedThread = await this.saveThreadAPI(
        threadId,
        title,
        description
      );

      runInAction(() => {
        this.updateThread(threadId, {
          title,
          description,
          isEphemeral: false,
        });
      });

      return savedThread;
    } catch (error) {
      this.setError(`saveThread-${threadId}`, error as Error);
      throw error;
    } finally {
      this.setLoading(`saveThread-${threadId}`, false);
      this.pendingOperations.delete(operationId);
    }
  }

  async archiveThread(threadId: number) {
    const operationId = `archive-thread-${threadId}`;
    this.pendingOperations.add(operationId);
    this.setLoading(`archiveThread-${threadId}`, true);

    try {
      await this.archiveThreadAPI(threadId);

      runInAction(() => {
        this.updateThread(threadId, { status: "archived" });
        if (this.threadFilter === "active") {
          this.threads = this.threads.filter((t) => t.id !== threadId);
        }
      });
    } catch (error) {
      this.setError(`archiveThread-${threadId}`, error as Error);
      throw error;
    } finally {
      this.setLoading(`archiveThread-${threadId}`, false);
      this.pendingOperations.delete(operationId);
    }
  }

  async deleteThread(threadId: number) {
    const operationId = `delete-thread-${threadId}`;
    this.pendingOperations.add(operationId);
    this.setLoading(`deleteThread-${threadId}`, true);

    try {
      await this.deleteThreadAPI(threadId);

      runInAction(() => {
        this.threads = this.threads.filter((t) => t.id !== threadId);
        if (this.currentThread?.id === threadId) {
          this.setCurrentThread(null);
        }
        this.messages.delete(threadId);
        this.messagePages.delete(threadId);
        this.typingUsers.delete(threadId);
      });
    } catch (error) {
      this.setError(`deleteThread-${threadId}`, error as Error);
      throw error;
    } finally {
      this.setLoading(`deleteThread-${threadId}`, false);
      this.pendingOperations.delete(operationId);
    }
  }

  async generateTitle(threadId: number) {
    const operationId = `generate-title-${threadId}`;
    this.pendingOperations.add(operationId);
    this.setLoading(`generateTitle-${threadId}`, true);

    try {
      const title = await this.generateTitleAPI(threadId);

      runInAction(() => {
        this.updateThread(threadId, { title });
      });

      return title;
    } catch (error) {
      this.setError(`generateTitle-${threadId}`, error as Error);
      throw error;
    } finally {
      this.setLoading(`generateTitle-${threadId}`, false);
      this.pendingOperations.delete(operationId);
    }
  }

  // Socket.io event listeners
  private initializeSocketListeners() {
    const client = (this.rootStore as any).client;
    if (!client) return;

    // Connection events
    client.io.on("connect", () => {
      runInAction(() => {
        this.setConnectionState(true);
      });
    });

    client.io.on("disconnect", () => {
      runInAction(() => {
        this.setConnectionState(false);
      });
    });

    client.io.on("reconnect", () => {
      runInAction(() => {
        this.setConnectionState(true);
        // Reload current thread messages after reconnection
        if (this.currentThreadId) {
          this.loadMessages(this.currentThreadId, true);
        }
      });
    });

    // Thread events
    const threadService = client.service("conversation-threads");

    const handleThreadCreated = (thread: ConversationThread) => {
      runInAction(() => {
        if (!this.threads.find((t) => t.id === thread.id)) {
          this.threads.unshift(thread);
        }
      });
    };
    threadService.on("created", handleThreadCreated);
    this.socketListeners.set("thread-created", handleThreadCreated);

    const handleThreadUpdated = (thread: ConversationThread) => {
      runInAction(() => {
        const index = this.threads.findIndex((t) => t.id === thread.id);
        if (index !== -1) {
          this.threads[index] = thread;
        }
        if (this.currentThread?.id === thread.id) {
          this.currentThread = thread;
        }
      });
    };
    threadService.on("updated", handleThreadUpdated);
    threadService.on("patched", handleThreadUpdated);
    this.socketListeners.set("thread-updated", handleThreadUpdated);

    const handleThreadRemoved = (thread: ConversationThread) => {
      runInAction(() => {
        this.threads = this.threads.filter((t) => t.id !== thread.id);
        if (this.currentThread?.id === thread.id) {
          this.currentThread = null;
        }
        this.messages.delete(thread.id);
        this.messagePages.delete(thread.id);
      });
    };
    threadService.on("removed", handleThreadRemoved);
    this.socketListeners.set("thread-removed", handleThreadRemoved);

    // Custom thread events
    const handleThreadShared = (data: {
      threadId: number;
      sharedWith: number[];
    }) => {
      runInAction(() => {
        this.updateThread(data.threadId, { sharedWith: data.sharedWith });
      });
    };
    threadService.on("thread-shared", handleThreadShared);
    this.socketListeners.set("thread-shared", handleThreadShared);

    const handleThreadArchived = (data: {
      threadId: number;
      status: string;
    }) => {
      runInAction(() => {
        this.updateThread(data.threadId, {
          status: data.status as "active" | "archived",
        });
        if (this.threadFilter === "active" && data.status === "archived") {
          this.threads = this.threads.filter((t) => t.id !== data.threadId);
        }
      });
    };
    threadService.on("thread-archived", handleThreadArchived);
    this.socketListeners.set("thread-archived", handleThreadArchived);

    const handleTitleGenerated = (data: {
      threadId: number;
      title: string;
    }) => {
      runInAction(() => {
        this.updateThread(data.threadId, { title: data.title });
      });
    };
    threadService.on("thread-title-generated", handleTitleGenerated);
    this.socketListeners.set("thread-title-generated", handleTitleGenerated);

    // Message events
    const messageService = client.service("chat-messages");

    const handleMessageCreated = (message: ChatMessage) => {
      runInAction(() => {
        // Don't add if it's our optimistic message
        if (!this.optimisticMessages.has(message.id)) {
          const threadMessages = this.messages.get(message.threadId) || [];
          if (!threadMessages.find((m) => m.id === message.id)) {
            this.messages.set(message.threadId, [...threadMessages, message]);

            // Update thread's message count and last message
            this.updateThread(message.threadId, {
              messageCount: threadMessages.length + 1,
              lastMessageAt: message.createdAt,
            });
          }
        }
      });
    };
    messageService.on("created", handleMessageCreated);
    this.socketListeners.set("message-created", handleMessageCreated);

    const handleMessageUpdated = (message: ChatMessage) => {
      runInAction(() => {
        const threadMessages = this.messages.get(message.threadId) || [];
        const index = threadMessages.findIndex((m) => m.id === message.id);
        if (index !== -1) {
          threadMessages[index] = message;
          this.messages.set(message.threadId, [...threadMessages]);
        }
      });
    };
    messageService.on("updated", handleMessageUpdated);
    messageService.on("patched", handleMessageUpdated);
    this.socketListeners.set("message-updated", handleMessageUpdated);

    const handleMessageRemoved = (message: ChatMessage) => {
      runInAction(() => {
        const threadMessages = this.messages.get(message.threadId) || [];
        this.messages.set(
          message.threadId,
          threadMessages.filter((m) => m.id !== message.id)
        );

        // Update thread's message count
        this.updateThread(message.threadId, {
          messageCount: Math.max(
            0,
            (this.currentThread?.messageCount || 0) - 1
          ),
        });
      });
    };
    messageService.on("removed", handleMessageRemoved);
    this.socketListeners.set("message-removed", handleMessageRemoved);

    // Streaming events
    const handleStreamStart = (data: {
      messageId: string;
      threadId: number;
    }) => {
      runInAction(() => {
        const message: ChatMessage = {
          id: data.messageId,
          threadId: data.threadId,
          content: "",
          role: "assistant",
          isStreaming: true,
          messageOrder: (this.messages.get(data.threadId)?.length || 0) + 1,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        this.streamingMessages.set(data.messageId, message);
        const threadMessages = this.messages.get(data.threadId) || [];
        this.messages.set(data.threadId, [...threadMessages, message]);
      });
    };
    messageService.on("stream-start", handleStreamStart);
    this.socketListeners.set("stream-start", handleStreamStart);

    const handleStreamChunk = (data: { messageId: string; chunk: string }) => {
      runInAction(() => {
        const message = this.streamingMessages.get(data.messageId);
        if (message) {
          this.updateStreamingMessage(
            data.messageId,
            message.content + data.chunk
          );
        }
      });
    };
    messageService.on("stream-chunk", handleStreamChunk);
    this.socketListeners.set("stream-chunk", handleStreamChunk);

    const handleStreamEnd = (data: { messageId: string; content: string }) => {
      runInAction(() => {
        this.updateStreamingMessage(data.messageId, data.content, true);
      });
    };
    messageService.on("stream-end", handleStreamEnd);
    this.socketListeners.set("stream-end", handleStreamEnd);

    // Typing events
    const handleTypingStart = (data: {
      threadId: number;
      userId: number;
      userName: string;
    }) => {
      if (data.userId !== (this.rootStore as any).authStore?.currentUser?.id) {
        runInAction(() => {
          this.addTypingUser(data.threadId, {
            userId: data.userId,
            userName: data.userName,
            startedAt: new Date(),
          });
        });
      }
    };
    messageService.on("typing-start", handleTypingStart);
    this.socketListeners.set("typing-start", handleTypingStart);

    const handleTypingStop = (data: { threadId: number; userId: number }) => {
      runInAction(() => {
        this.removeTypingUser(data.threadId, data.userId);
      });
    };
    messageService.on("typing-stop", handleTypingStop);
    this.socketListeners.set("typing-stop", handleTypingStop);
  }

  // Helper methods for API calls
  private async getThreadsFromStore(includeArchived: boolean): Promise<ConversationThread[]> {
    // Try to get the store if it wasn't available during construction
    if (!this.threadStore) {
      this.threadStore = (this.rootStore as any).conversationThreadsStore || null;
    }
    
    if (!this.threadStore) {
      console.warn("Thread store not initialized yet, returning empty array");
      return [];
    }

    const response = await this.threadStore.find({
      query: {
        status: includeArchived ? undefined : "active",
        $sort: { lastMessageAt: -1 },
      },
    });

    return (response?.data || response || []) as unknown as ConversationThread[];
  }

  private async getSharedThreadsFromStore(): Promise<ConversationThread[]> {
    // Try to get the store if it wasn't available during construction
    if (!this.threadStore) {
      this.threadStore = (this.rootStore as any).conversationThreadsStore || null;
    }
    
    if (!this.threadStore) {
      console.warn("Thread store not initialized yet, returning empty array");
      return [];
    }

    const response = await this.threadStore.find({
      query: {
        shared: true,
        $sort: { lastMessageAt: -1 },
      },
    });

    return (response?.data || response || []) as unknown as ConversationThread[];
  }

  private async createThreadAPI(data: { title?: string; description?: string; isEphemeral?: boolean }): Promise<ConversationThread> {
    if (!this.threadStore) {
      throw new Error("Thread store not initialized");
    }

    const threadData = {
      isEphemeral: true,
      ...data,
    };

    return (await this.threadStore.create(threadData)) as unknown as ConversationThread;
  }

  private async getThreadMessagesFromStore(
    threadId: number,
    limit: number,
    offset: number
  ): Promise<ChatMessage[]> {
    const chatMessagesStore = (this.rootStore as any)?.chatMessagesStore as ServiceStore;
    if (!chatMessagesStore) {
      throw new Error("Chat messages store not available");
    }

    const response = await chatMessagesStore.find({
      query: {
        threadId,
        $sort: { messageOrder: 1 },
        $limit: limit,
        $skip: offset,
      },
    });

    const result = response?.data || response || [];
    return Array.isArray(result) ? (result as unknown as ChatMessage[]) : [];
  }

  private async saveThreadAPI(
    threadId: number,
    title: string,
    description?: string
  ): Promise<ConversationThread> {
    if (!this.threadStore) {
      throw new Error("Thread store not initialized");
    }

    // Ensure threadId is a valid number
    const id = Number(threadId);
    if (isNaN(id)) {
      throw new Error(`Invalid thread ID: ${threadId}`);
    }

    // Prepare patch data with proper types
    const patchData = {
      title: title.trim(),
      description: description?.trim() || null,
      isEphemeral: false,
    };

    console.log("ConversationStore: Saving thread with data:", { id, patchData });

    try {
      const result = await this.threadStore.patch(id, patchData);
      
      if (!result) {
        throw new Error("Failed to save thread - no response from server");
      }

      return result as unknown as ConversationThread;
    } catch (error) {
      console.error("ConversationStore: Error saving thread:", error);
      throw error;
    }
  }

  private async archiveThreadAPI(threadId: number): Promise<void> {
    if (!this.threadStore) {
      throw new Error("Thread store not initialized");
    }

    await this.threadStore.patch(threadId, { status: "archived" });
  }

  private async deleteThreadAPI(threadId: number): Promise<void> {
    if (!this.threadStore) {
      throw new Error("Thread store not initialized");
    }

    await this.threadStore.remove(threadId);
  }

  private async searchThreadsAPI(searchTerm: string): Promise<ConversationThread[]> {
    if (!this.threadStore) {
      throw new Error("Thread store not initialized");
    }

    const response = await this.threadStore.find({
      query: {
        $or: [
          { title: { $regex: searchTerm, $options: "i" } },
          { description: { $regex: searchTerm, $options: "i" } },
        ],
        $sort: { lastMessageAt: -1 },
      },
    });

    return (response?.data || response || []) as unknown as ConversationThread[];
  }

  private async generateTitleAPI(threadId: number): Promise<string> {
    if (!this.threadStore) {
      throw new Error("Thread store not initialized");
    }

    // Call a custom service method to generate title
    const client = (this.rootStore as Record<string, unknown>).client as {
      service: (name: string) => {
        generateTitle: (id: number) => Promise<{ title: string }>;
      };
    };
    if (!client) {
      throw new Error("Feathers client not available");
    }

    const response = await client
      .service("conversation-threads")
      .generateTitle(threadId);

    return response.title;
  }

  // Emit typing events
  emitTypingStarted(threadId: number) {
    const client = (this.rootStore as any).client;
    if (!client) return;

    client.service("chat-messages").emit("typing-start", {
      threadId,
      userId: (this.rootStore as any).authStore?.currentUser?.id,
      userName: (this.rootStore as any).authStore?.currentUser?.name || "User",
    });
  }

  emitTypingStopped(threadId: number) {
    const client = (this.rootStore as any).client;
    if (!client) return;

    client.service("chat-messages").emit("typing-stop", {
      threadId,
      userId: (this.rootStore as any).authStore?.currentUser?.id,
    });
  }

  // Setup reactions for automatic behaviors
  private setupReactions() {
    // Auto-load messages when current thread changes
    reaction(
      () => this.currentThreadId,
      (threadId) => {
        if (threadId && !this.messages.has(threadId)) {
          this.loadMessages(threadId);
        }
      }
    );

    // Clean up typing timeouts when thread changes
    reaction(
      () => this.currentThreadId,
      (_threadId, previousThreadId) => {
        if (previousThreadId) {
          this.emitTypingStopped(previousThreadId);
        }
      }
    );

    // Auto-save ephemeral threads after first message
    when(
      () => {
        if (!this.currentThread?.isEphemeral) return false;
        const messages = this.messages.get(this.currentThread.id) || [];
        return messages.length > 0;
      },
      () => {
        // Prompt for saving will be handled by UI components
        console.log("Ephemeral thread has messages, consider saving");
      }
    );
  }

  // Cleanup
  dispose() {
    // Clear all timeouts
    this.typingTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.typingTimeouts.clear();

    // Remove socket listeners
    const client = (this.rootStore as any).client;
    if (client) {
      this.socketListeners.forEach((handler, event) => {
        const [service, eventName] = event.split("-");
        if (service === "thread") {
          client.service("conversation-threads").off(eventName, handler as (() => void));
        } else if (
          service === "message" ||
          service === "stream" ||
          service === "typing"
        ) {
          client.service("chat-messages").off(eventName, handler as (() => void));
        }
      });
    }
    this.socketListeners.clear();
  }

  // Computed values - Thread state
  get isThreadsLoading() {
    return this.loadingStates.get(`threads-${this.threadFilter}`) || false;
  }

  get threadsError() {
    return this.errors.get(`threads-${this.threadFilter}`);
  }

  get visibleThreads() {
    if (this.isSearching || this.searchResults.length > 0) {
      return this.searchResults;
    }
    return this.threads;
  }

  get activeThreads() {
    return this.threads.filter((t) => t.status === "active");
  }

  get archivedThreads() {
    return this.threads.filter((t) => t.status === "archived");
  }

  get ephemeralThreads() {
    return this.threads.filter((t) => t.isEphemeral);
  }

  // Computed values - Message state
  get currentThreadMessages() {
    if (!this.currentThread) return [];
    const messages = this.messages.get(this.currentThread.id) || [];
    // Sort by messageOrder to ensure correct sequence
    return messages.slice().sort((a, b) => a.messageOrder - b.messageOrder);
  }

  get isCurrentThreadLoading() {
    if (!this.currentThread) return false;
    return this.loadingStates.get(`messages-${this.currentThread.id}`) || false;
  }

  get currentThreadError() {
    if (!this.currentThread) return null;
    return this.errors.get(`messages-${this.currentThread.id}`);
  }

  get canLoadMoreMessages() {
    if (!this.currentThread) return false;
    const pageInfo = this.messagePages.get(this.currentThread.id);
    return pageInfo?.hasMore || false;
  }

  get isSendingMessage() {
    if (!this.currentThread) return false;
    return (
      this.loadingStates.get(`sendMessage-${this.currentThread.id}`) || false
    );
  }

  // Computed values - Typing indicators
  get currentThreadTypingUsers() {
    if (!this.currentThread) return [];
    return this.typingUsers.get(this.currentThread.id) || [];
  }

  get typingUsersText() {
    const users = this.currentThreadTypingUsers;
    if (users.length === 0) return "";
    if (users.length === 1) return `${users[0].userName} is typing...`;
    if (users.length === 2)
      return `${users[0].userName} and ${users[1].userName} are typing...`;
    return `${users[0].userName} and ${users.length - 1} others are typing...`;
  }

  // Computed values - UI state
  get hasUnsavedChanges() {
    return (
      this.currentThread?.isEphemeral &&
      (this.currentThreadMessages.length > 0 ||
        this.optimisticMessages.size > 0)
    );
  }

  get hasPendingOperations() {
    return this.pendingOperations.size > 0;
  }

  get isAnyOperationLoading() {
    return this.loadingStates.size > 0;
  }

  get allErrors() {
    return Array.from(this.errors.entries()).map(([key, error]) => ({
      key,
      error,
      message: error.message,
    }));
  }
}

export default ConversationStore;
